import { useMutation } from '@apollo/client/react';
import { type BasicCouponFragment, ValidateCouponDocument } from '@drycut/graphql';
import { Button, Modal, Para, Stack } from '@drycut/ui-components';
import { PlusIcon, TagIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { useCheckoutStore } from '../BookingCheckout/checkoutStore';
import CouponForm from './CouponForm';
import CouponMessage from './CouponMessage';

type Props = {
  disabled?: boolean;
  onValidate: (coupon: BasicCouponFragment) => void;
};

// TODO: Maybe rename after we remove the CouponInsert component?
export function NewCouponInsert({ onValidate, disabled = false }: Props): React.ReactElement {
  const { resetField } = useFormContext();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const selectedCoupon = useCheckoutStore((state) => state.selectedCoupon);
  const setSelectedCoupon = useCheckoutStore((state) => state.setSelectedCoupon);
  const [searchParams] = useSearchParams();
  const phoneNumber = useWatch({ name: 'phone' });

  const code = searchParams.get('coupon');

  const [validateCoupon] = useMutation(ValidateCouponDocument);

  // Validate coupon code in the query string (present when the customer comes from a referral link)
  useEffect(() => {
    if (code) {
      validateCoupon({
        variables: {
          input: {
            phoneNumber,
            code,
          },
        },
      }).then(({ data }) => {
        const validatedCoupon = data?.validateCoupon?.coupon;

        if (validatedCoupon) {
          setSelectedCoupon(validatedCoupon);
          onValidate(validatedCoupon);
        }
      });
    }
  }, [code, phoneNumber, validateCoupon, onValidate, setSelectedCoupon]);

  const closeModal = () => {
    resetField('couponCode');
    setModalOpen(false);
  };

  const handleValidated = (coupon: BasicCouponFragment) => {
    setSelectedCoupon(coupon);
    setModalOpen(false);
    onValidate(coupon);
  };

  return (
    <>
      {!selectedCoupon && (
        <Button type="button" fill="ghost" onClick={() => setModalOpen(true)} disabled={disabled}>
          <Stack direction="row" gap={1}>
            <PlusIcon className="size-4" />

            <Para size="sm">
              Hier <u>Rabattcode</u> eingeben
            </Para>
          </Stack>
        </Button>
      )}

      {selectedCoupon && (
        <Stack gap={1}>
          <Stack direction="row" gap={2}>
            <TagIcon className="w-4 h-4 text-primary-600" />
            <Para className="text-primary-600" weight="medium">
              {selectedCoupon.code}
            </Para>
          </Stack>

          <CouponMessage couponInfo={selectedCoupon} />
        </Stack>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Gift Card Code eingeben"
        overlayClose={false}
        closeButton
      >
        <CouponForm onCancel={closeModal} onValidate={handleValidated} />
      </Modal>
    </>
  );
}
